import {FilterMatchMode, SelectItem, SortEvent} from "primeng/api";
import {TranslationUtil} from "./translation-util";
import * as XLSX from "xlsx";
import {GlobalUtils} from "./global-utils";
import { formatDate } from "@angular/common";
import {environment} from "../../environments/environment";
import { ConfigurationService } from "../services/configuration.service";


const EXEL_EXTENSION = "xlsx";

/**
 * The `TableUtils` class provides common configurations for PrimeNG DataTables.
 * It is recommended to use the filter configurations provided by this class within HTML files.
 *
 * This class includes utility methods for generating options for paginator's "rowsPerPageOptions,"
 * as well as date and column filter match mode options for DataTable columns.
 *
 * Note: It is advised to refer to the documentation and method comments for specific details on usage.
 */
export class TableUtils<T> {

  public static readonly ROWS_PER_PAGE: number = 5;
  public static readonly ROWS_PER_PAGE_OPTIONS: number[] = [5, 10, 25, 50];

  /**
   * The TableUtils class contains the common different p-table input's configs.
   * It's better to use the configs within HTML files.
   ***/

  private static filterTranslationKeys: any;
  private static filterDateTranslationKeys: any;
  private static filterMatchModeTranslationKeys: any;
  public static configurationService: ConfigurationService;


  filterMatchModeOptions = {
    text: [
      FilterMatchMode.STARTS_WITH,
      FilterMatchMode.CONTAINS,
      FilterMatchMode.NOT_CONTAINS,
      FilterMatchMode.ENDS_WITH,
      FilterMatchMode.EQUALS,
      FilterMatchMode.NOT_EQUALS
    ],
    numeric: [
      FilterMatchMode.EQUALS,
      FilterMatchMode.NOT_EQUALS,
      FilterMatchMode.LESS_THAN,
      FilterMatchMode.LESS_THAN_OR_EQUAL_TO,
      FilterMatchMode.GREATER_THAN,
      FilterMatchMode.GREATER_THAN_OR_EQUAL_TO
    ],
    date: [
      FilterMatchMode.DATE_IS,
      FilterMatchMode.DATE_IS_NOT,
      FilterMatchMode.DATE_BEFORE,
      FilterMatchMode.DATE_AFTER
    ]
  };

  /**
   * @deprecated No longer in use.
   * Use this method to generate an array of options for the paginator's "rowsPerPageOptions" property in a PrimeNG DataTable.
   * The method calculates the options based on the total number of elements, elements per page, and a provided array.
   *
   * @param totalElements - The total number of elements in the dataset.
   * @param elementsPerPage - The number of elements to be displayed per page.
   * @param array - An array to store the calculated options. Pass an empty array or an array with initial options.
   * @returns An array of numeric values representing options for the paginator's "rowsPerPageOptions."
   */

    static setConfigurationService(configurationService: ConfigurationService): void {
      this.configurationService = configurationService;
    }

  public static getRowsPerPageOptions(totalElements: number, elementsPerPage: number, array: number[]): number[] {
    // Check if pagination is needed and the parameters are valid
    if (totalElements > elementsPerPage && elementsPerPage !== 0 && totalElements !== 0) {
      // Adjust totalElements to a multiple of 5 greater than the current value
      if (totalElements % elementsPerPage > 0) {
        totalElements = Math.floor(totalElements / elementsPerPage) * 5 + 5;
      }
      // If the array is empty, push the totalElements value
      if (array.length === 0) {
        array.push(totalElements);
      }
      // Add the previous multiple of elementsPerPage to the array
      array.unshift(totalElements -= elementsPerPage);

      // Recursively call the function for further options
      return this.getRowsPerPageOptions(totalElements, elementsPerPage, array);
    }
    // If no pagination is needed or the array is empty, push elementsPerPage to the array
    if (!array.length) {
      array.push(elementsPerPage);
    }
    return array;
  }

  public static pColumnFilterTranslate(): any {
    this.filterTranslationKeys = TranslationUtil.translate('P_TABLE_CONFIG.P_COLUMN_FILTER_CONFIG');
    return this.filterTranslationKeys ? {
      apply: this.filterTranslationKeys.APPLY,
      clear: this.filterTranslationKeys.CLEAR,
      addRule: this.filterTranslationKeys.ADD_RULE,
      removeRule: this.filterTranslationKeys.REMOVE_RULE,
      emptyMessage: this.filterTranslationKeys.EMPTY_MESSAGE,
      emptyFilterMessage: this.filterTranslationKeys.EMPTY_FILTER_MESSAGE
    } : {}
  }

  /**
   * Generates an array of SelectItem objects representing filter match modes for PrimeNG DataTable columns.
   * The translation keys for filter labels are retrieved from the translation service.
   *
   * @returns An array of SelectItem objects with label and value properties representing filter match modes.
   * If translation keys are not available, an empty array is returned.
   */
  public static pColumnFilterMatchMode(): SelectItem[] {
    this.filterMatchModeTranslationKeys = TranslationUtil.translate('P_TABLE_CONFIG.P_COLUMN_FILTER_MATCH_MODE_CONFIG');
    return this.filterMatchModeTranslationKeys ? [
      {
        label: this.filterMatchModeTranslationKeys.STARTS_WITH,
        value: FilterMatchMode.STARTS_WITH
      },
      {
        label: this.filterMatchModeTranslationKeys.ENDS_WITH,
        value: FilterMatchMode.ENDS_WITH
      },
      {
        label: this.filterMatchModeTranslationKeys.CONTAINS,
        value: FilterMatchMode.CONTAINS
      },
      {
        label: this.filterMatchModeTranslationKeys.NOT_CONTAINS,
        value: FilterMatchMode.NOT_CONTAINS
      },
      {
        label: this.filterMatchModeTranslationKeys.EQUALS,
        value: FilterMatchMode.EQUALS
      },
      {
        label: this.filterMatchModeTranslationKeys.NOT_EQUALS,
        value: FilterMatchMode.NOT_EQUALS
      }
    ] : []
  }

  /**
   * Returns an array of `SelectItem` objects representing the "contains" match mode
   * for numeric filters in PrimeNG DataTable columns.
   *
   * The method retrieves translation labels using the `TranslationUtil` service.
   * If the translation keys are unavailable, an empty array is returned.
   *
   * @returns An array of `SelectItem` objects with `label` and `value` properties.
   *          If translation keys are not found, an empty array is returned.
   */

  public static pColumnFilterContainsMatchMode(): SelectItem[] {
    this.filterMatchModeTranslationKeys = TranslationUtil.translate('P_TABLE_CONFIG.P_COLUMN_FILTER_MATCH_MODE_CONFIG');
    return this.filterMatchModeTranslationKeys ? [
      {
        label: this.filterMatchModeTranslationKeys.CONTAINS,
        value: FilterMatchMode.CONTAINS
      }
    ] : []
  };

  /**
   * Generates an array of SelectItem objects representing numeric filter match modes for PrimeNG DataTable columns.
   * The translation keys for filter labels are retrieved from the translation service.
   *
   * @returns An array of SelectItem objects with label and value properties representing numeric filter match modes.
   * If translation keys are not available, an empty array is returned.
   */

  public static pNumericColumnFilterMatchMode(): SelectItem[] {
    this.filterMatchModeTranslationKeys = TranslationUtil.translate('P_TABLE_CONFIG.P_COLUMN_FILTER_NUMERIC_CONFIG');
    return this.filterMatchModeTranslationKeys ? [
      {
        label: this.filterMatchModeTranslationKeys.EQUALS,
        value: FilterMatchMode.EQUALS
      },
      {
        label: this.filterMatchModeTranslationKeys.NOT_EQUALS,
        value: FilterMatchMode.NOT_EQUALS
      },
      {
        label: this.filterMatchModeTranslationKeys.LESS_THAN,
        value: FilterMatchMode.LESS_THAN
      },
      {
        label: this.filterMatchModeTranslationKeys.LESS_THAN_OR_EQUAL_TO,
        value: FilterMatchMode.LESS_THAN_OR_EQUAL_TO
      },
      {
        label: this.filterMatchModeTranslationKeys.GREATER_THAN,
        value: FilterMatchMode.GREATER_THAN
      },
      {
        label: this.filterMatchModeTranslationKeys.GREATER_THAN_OR_EQUAL_TO,
        value: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO
      }
    ] : []
  }

  /**
   * Generates an array of SelectItem objects representing date filter match modes for PrimeNG DataTable columns.
   * The translation keys for filter labels are retrieved from the translation service.
   *
   * @returns An array of SelectItem objects with label and value properties representing date filter match modes.
   * If translation keys are not available, an empty array is returned.
   */
  public static pDateColumnFilterMatchMode(): SelectItem[] {
    this.filterDateTranslationKeys = TranslationUtil.translate('P_TABLE_CONFIG.P_COLUMN_FILTER_DATE_CONFIG');
    return this.filterDateTranslationKeys ? [
      {
        label: this.filterDateTranslationKeys.DATE_IS,
        value: FilterMatchMode.DATE_IS
      },
      {
        label: this.filterDateTranslationKeys.DATE_IS_NOT,
        value: FilterMatchMode.DATE_IS_NOT
      },
      {
        label: this.filterDateTranslationKeys.DATE_BEFORE,
        value: FilterMatchMode.DATE_BEFORE
      },
      {
        label: this.filterDateTranslationKeys.DATE_AFTER,
        value: FilterMatchMode.DATE_AFTER
      }
    ] : [];
  }

  /**
   * Exports data to an Excel file with customized column names.
   *
   * @param data - The array of data to be exported: define a new model of data to represent the excel model.
   * @param columnNames - An array of strings representing the custom column names in order.
   * @param fileName - The desired name for the Excel file (without extension).
   * @param sheetName - (Optional) The name of the Excel sheet. If not provided, the data will be exported to a default sheet.
   * @returns void
   */
   public static exportToExcel(data: any[], columnNames: string[], fileName: string, sheetName?: string): void {
      // Map data to include only properties specified in columnNames

    let mappedData: Record<string, any>[] = [];

  if (data && data.length > 0) {
    mappedData = data.map(item => {
      const mappedItem: Record<string, any> = {};
      columnNames.forEach((columnName, index) => {
        mappedItem[columnName] = item[Object.keys(data[0])[index]];
      });
      return mappedItem;
    });
  } else {
    // Create a single empty row with all column names as keys and empty values
    const emptyRow: Record<string, any> = {};
    columnNames.forEach(col => emptyRow[col] = '');
    mappedData = [emptyRow];
  }

   // Convert the mapped array of data to an Excel sheet with custom column names
  const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(mappedData);

  // Create a new Excel workbook
  const wb: XLSX.WorkBook = XLSX.utils.book_new();
 // Append the sheet to the workbook if a sheetName is provided
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  XLSX.writeFile(wb, `${fileName}.${EXEL_EXTENSION}`);
}

    public static exportToPdf(
        data: any[],
        columnDefinitions: { header: string; dataKey: string }[],
        fileName: string,
        formatCellFn?: (key: string, value: any) => any
    ): void {
        import('jspdf').then(jsPDF => {
            import('jspdf-autotable').then(() => {
                const doc = new jsPDF.default();
                const pdfParametre= this.configurationService.get().PDF;

                const formattedData = data.map(item => {
                    const formattedItem: Record<string, any> = {};
                    for (const key of Object.keys(item)) {
                        if (formatCellFn) {
                            formattedItem[key] = formatCellFn(key, item[key]);
                        } else {
                            formattedItem[key] = item[key];
                        }
                    }
                    return formattedItem;
                });

                doc.autoTable({
                    columns: columnDefinitions,
                    body: formattedData,
                    styles: {fontSize: 5},
                    headStyles: { fillColor: pdfParametre.COLOR.BACKGROUND, color: pdfParametre.COLOR.TEXT }
                });
                doc.save(`${fileName}.pdf`);
            });
        });
    }

  /**
   * Checks if two arrays have the same unique elements.
   *
   * @param array1 - The first array.
   * @param array2 - The second array.
   * @returns `true` if the arrays have the same unique elements; otherwise, `false`.
   *
   * @example
   * const result = arraysHaveSameUniqueElements([1, 2, 3], [3, 2, 1]); // true
   */

  public static arraysHaveSameUniqueElements<T>(array1: T[], array2: T[]): boolean {
    const set1 = new Set(array1);
    const set2 = new Set(array2);
    if (set1.size !== set2.size) {
      return false;
    }
    return [...set1].every(item => set2.has(item));
  }

  /**
   * Imports an Excel file and maps its data to JSON objects.
   *
   * @param file - The Excel file.
   * @param headerMapping - Mapping of Excel headers to object properties.
   * @returns A promise resolving to an array of objects of type `T`.
   *
   * @example
   * importFromExcel<User>(file, { "Name": "name", "Age": "age" })
   *     .then(data => console.log(data));
   *
   * @throws Error if the file is invalid or cannot be processed.
   */
  public static importFromExcel<T>(file: File, headerMapping: { [key: string]: keyof T }): Promise<T[]> {
    return new Promise<T[]>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        try {
          const binaryString = event.target?.result as string;
          const workbook = XLSX.read(binaryString, {type: 'binary'});
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          // Convert sheet to JSON, treating the first row as headers
          const data: any[] = XLSX.utils.sheet_to_json(worksheet, {header: 1});

          // Validate column names
          if (this.isNonValidAttributes<T>(data, headerMapping)) {
            throw new Error("Invalid File");
          }

          // Extract the headers from the first row
          const [headers, ...rows] = data;

          // Check rows if empty
          if(this.isEmptyExcel(rows)){
            throw new Error("Empty File");
          }

          // Create a mapping from headers to model attributes
          const attributeMapping = headers.reduce((acc, header) => {
            if (headerMapping[header]) {
              acc[header] = headerMapping[header];
            }
            return acc;
          }, {} as { [header: string]: keyof T });

          // Transform rows to conform to type T
          const processedData: T[] = rows.map(row => {
            const result: Partial<T> = {};
            headers.forEach((header, index) => {
              if (attributeMapping[header] !== undefined) {
                result[attributeMapping[header]] = row[index];
              }
            });
            return result as T;
          });

          resolve(processedData);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsBinaryString(file);
    });
  }

  /**
   * Checks if the headers in the data match the expected attributes.
   *
   * @param data - The Excel data array (first row contains headers).
   * @param headerMapping - Expected header mapping.
   * @returns `true` if there are non-valid attributes; otherwise, `false`.
   *
   * @example
   * const result = isNonValidAttributes(["Name", "Age"], { "Name": "name", "Age": "age" }); // false
   */
  private static isNonValidAttributes<T>(data: any[], headerMapping: { [key: string]: keyof T }): boolean {
    const requiredAttributes = Object.keys(headerMapping);
    const mappedAttributes = Object.values(data[0]);
    return !this.arraysHaveSameUniqueElements(mappedAttributes, requiredAttributes);
  }

  private static isEmptyExcel(data: any[]): boolean {
    return GlobalUtils.isEmpty(data.filter(value => !GlobalUtils.isEmpty(value)));
  }

/**
   * Get French month short name from a provided month list (e.g., Avr, Mai, Juin)
   */
public static getFrenchMonthList(monthIndex: number, frenchMonths: string[]): string {

  if (Array.isArray(frenchMonths) && frenchMonths.length === 12) {
    return frenchMonths[monthIndex];
  } else {
    console.error('Invalid month names array');
    return '';
  }
}

 /**
 * Format a timestamp to 'dd month, HH:mm' format (French months)
 * // exemple  "30 Fév, 11:37"
 */
 public static formatDateToDayMonthTime(timestamp: string, frenchMonths: string[]): string {
  if (!timestamp) {
    return '--:--';
  }
  const date = new Date(timestamp);
  const day = date.getDate();
  const month = TableUtils.getFrenchMonthList(date.getMonth(), frenchMonths); // Use provided months
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${day} ${month}, ${hours}:${minutes}`;
}

 /**
 * Format a timestamp to 'dd/MM/yyyy' format
 * exemple "31/02/2027"
 */
 public static formatDateToDayMonthYear(timestamp: string): string {
  return formatDate(timestamp, 'dd/MM/yyyy', 'fr-FR');
}

/**
* Generic custom sort function for PrimeNG p-table
* Can sort any Formatted dates (using raw fields) and normal fields
*
* @param event PrimeNG sort event
* @param dateFields List of fields that should be sorted as dates (e.g., ['startTime', 'endTime', '...', ...])
*/
public static customSortWithDateFields(event: SortEvent, dateFields: string[] = []): void {
event.data.sort((data1, data2) => {
  let value1;
  let value2;

  // If field is in dateFields => use corresponding Raw field and convert to timestamp
  if (dateFields.includes(event.field)) {
    value1 = new Date(data1[`${event.field}Raw`]).getTime();
    value2 = new Date(data2[`${event.field}Raw`]).getTime();
  } else {
    value1 = data1[event.field];
    value2 = data2[event.field];
  }

  let result = 0;
  if (value1 == null && value2 != null) result = -1;
  else if (value1 != null && value2 == null) result = 1;
  else if (value1 == null && value2 == null) result = 0;
  else if (value1 < value2) result = -1;
  else if (value1 > value2) result = 1;
  else result = 0;

  return event.order * result;
});
}

}
